/**
 * PendingApprovalsTable — the admin control room's cross-project approvals
 * inbox (mockup `#s-actrl` "Pending approvals" card): local `<table>`,
 * fed by `AdminControlRoom` filtering enrollments to `state ===
 * "pending_approval"` across every shown project.
 *
 * CRITICAL (spec §5.2, Andrea's explicit fix): the "Finished" column is a
 * **plain text label only** ("Demo · 20 cases" / "Phase 1 · 100 cases") —
 * never a mini-pipeline visualization (bar segments / per-stage dots). An
 * earlier draft rendered that structure here; don't reintroduce it.
 *
 * Agreement values come from the enrollment's/batch's scorecard when
 * present. Per feedback_no_silent_fallbacks: a missing/absent value renders
 * "—", never a fabricated 0.00 — `AdminControlRoom` is responsible for
 * passing `agreement: null` rather than guessing, since today's `Enrollment`
 * type (src/types/factory.ts) has no guaranteed scorecard field.
 */

import { StatusBadge } from "../primitives";

export interface PendingApprovalRow {
  project_id: string;
  annotator: string;
  /** Plain text label for the finished phase, e.g. "Demo · 20 cases". */
  finishedLabel: string;
  /** Plain text label for the phase this approval advances to. */
  advancesToLabel: string;
  /** 0..1 agreement score, or null when no scorecard is available yet. */
  agreement: number | null;
  agreementNote?: string;
  /** Set by AdminControlRoom after a 409 on this row's approve mutation. */
  poolExhausted?: boolean;
}

export interface PendingApprovalsTableProps {
  rows: PendingApprovalRow[];
  onApprove: (row: PendingApprovalRow) => void;
}

function AgreementCell({
  agreement,
  agreementNote,
}: Pick<PendingApprovalRow, "agreement" | "agreementNote">) {
  if (agreement == null) {
    return <span className="text-sm text-fg-faint">—</span>;
  }
  const low = agreement < 0.8;
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-2">
        <span className="block h-1 w-[70px] overflow-hidden rounded-sharp bg-white/[0.06]">
          <span
            className={`block h-full rounded-sharp ${low ? "bg-warn" : "bg-primary"}`}
            style={{ width: `${Math.round(agreement * 100)}%` }}
          />
        </span>
        <span className={`font-mono text-[11.5px] ${low ? "text-warn" : "text-fg-muted"}`}>
          {agreement.toFixed(2)}
        </span>
      </span>
      {agreementNote && <span className="text-[11px] text-fg-faint">{agreementNote}</span>}
    </div>
  );
}

export function PendingApprovalsTable({ rows, onApprove }: PendingApprovalsTableProps) {
  return (
    <div className="rounded border border-border/50 bg-surface">
      <div className="flex items-center gap-3 border-b border-border/50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-fg">Pending approvals</h3>
        <span className="font-mono text-xs text-fg-muted">{rows.length}</span>
        <span className="flex-1" />
        <span className="text-[11.5px] text-fg-faint">
          gated advancements only · demo→P1, P1→P2
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-fg-muted">No pending approvals</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-border/50 text-[11px] uppercase tracking-wide text-fg-dim">
                <th className="px-5 py-2.5 font-medium">Annotator</th>
                <th className="px-5 py-2.5 font-medium">Project</th>
                <th className="px-5 py-2.5 font-medium">Finished</th>
                <th className="px-5 py-2.5 font-medium">Advances to</th>
                <th className="w-[200px] px-5 py-2.5 font-medium">Agreement</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const atIdx = row.annotator.indexOf("@");
                const handle = atIdx > 0 ? row.annotator.slice(0, atIdx) : row.annotator;
                return (
                  <tr
                    key={`${row.project_id}-${row.annotator}`}
                    className="border-b border-border/50 last:border-b-0"
                  >
                    <td className="px-5 py-3.5 align-top">
                      <div className="text-sm text-fg">{handle}</div>
                      <div className="text-xs text-fg-muted">{row.annotator}</div>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <span className="font-mono text-xs text-fg-muted">{row.project_id}</span>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <StatusBadge tone="ghost">{row.finishedLabel}</StatusBadge>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <StatusBadge tone="sig">{row.advancesToLabel}</StatusBadge>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <AgreementCell agreement={row.agreement} agreementNote={row.agreementNote} />
                    </td>
                    <td className="px-5 py-3.5 text-right align-top">
                      <div className="flex flex-col items-end gap-1.5">
                        {row.poolExhausted && (
                          <span className="text-[11px] text-warn">
                            Pool empty — nothing to advance into
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => onApprove(row)}
                          className="rounded bg-primary/15 px-3 py-1.5 text-xs font-semibold text-signal-text-tint transition hover:bg-primary/25"
                        >
                          Review &amp; approve
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PendingApprovalsTable;
